import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

/**
 * Shim interne de la librairie morte `@flxng/circle-timer`.
 * API identique à la lib d'origine : [duration] (ms), [color], (onComplete),
 * méthodes publiques start(startDate?), init(), isTicking().
 * Rendu : anneau SVG circulaire avec décompte du temps restant au centre.
 * Le CSS global V1 stylise `.base-timer` et `.time`.
 */
@Component({
  selector: 'flx-circle-timer',
  standalone: false,
  template: `
    <div class="base-timer">
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        <circle class="base-timer__path-remaining"
                cx="50" cy="50" r="45"
                fill="none"
                [attr.stroke]="color"
                stroke-width="7"
                stroke-linecap="round"
                transform="rotate(-90 50 50)"
                [attr.stroke-dasharray]="dashArray"></circle>
      </svg>
      <span class="time">{{ timeLeftLabel }}</span>
    </div>
  `,
  styles: [`
    :host {
      display: inline-block;
    }
    .base-timer {
      position: relative;
    }
    .base-timer svg {
      width: 100%;
      height: 100%;
    }
  `]
})
export class CircleTimerComponent implements OnDestroy {
  @Input() duration: number; // millisecondes
  @Input() color = '#28a745';
  @Output() onComplete = new EventEmitter<void>();

  timeLeftLabel = '';
  dashArray = '283 283';

  private static readonly FULL_DASH_ARRAY = 283; // 2 * PI * 45
  private _startDate: number = null;
  private _remaining = 0;
  private _ticking = false;
  private _intervalId: any = null;

  init(): void {
    this._stop();
    this._startDate = null;
    this._remaining = this.duration || 0;
    this._render();
  }

  start(startDate?: number): void {
    this._stop();
    this._startDate = startDate !== undefined && startDate !== null ? startDate : Date.now();
    this._ticking = true;
    this._tick();
    this._intervalId = setInterval(() => this._tick(), 250);
  }

  isTicking(): boolean {
    return this._ticking;
  }

  ngOnDestroy(): void {
    this._stop();
  }

  private _tick(): void {
    const elapsed = Date.now() - this._startDate;
    this._remaining = Math.max((this.duration || 0) - elapsed, 0);
    this._render();

    if (this._remaining <= 0) {
      this._stop();
      this.onComplete.emit();
    }
  }

  private _stop(): void {
    if (this._intervalId !== null) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
    this._ticking = false;
  }

  private _render(): void {
    const totalSeconds = Math.ceil(this._remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    this.timeLeftLabel = (minutes < 10 ? '0' + minutes : '' + minutes) + ':' + (seconds < 10 ? '0' + seconds : '' + seconds);

    const fraction = this.duration > 0 ? this._remaining / this.duration : 0;
    const dash = (fraction * CircleTimerComponent.FULL_DASH_ARRAY).toFixed(0);
    this.dashArray = dash + ' ' + CircleTimerComponent.FULL_DASH_ARRAY;
  }
}
